import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Star, ArrowRight, CheckCircle, Users, Award, Clock, Phone, Mail, ChefHat, Bath, Home as HomeIcon, Square, Zap, Palette } from "lucide-react";
import { testimonials, companyInfo, getServices, getCompanyValues } from "../mock";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";

const Home = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  
  const services = getServices(language);
  const companyValues = getCompanyValues(language);
  
  const iconMap = {
    ChefHat,
    Bath,
    Home: HomeIcon,
    Square,
    Zap,
    Palette
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        {/* Background with your real work images */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black bg-opacity-50 z-10"></div>
          <img 
            src="https://customer-assets.emergentagent.com/job_aymorenov-catalog/artifacts/mp9znoxl_Green%20and%20White%20Modern%20Home%20Renovation%20Instagram%20Post.png"
            alt="Travaux AYMORENOV"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex flex-col items-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_aymorenov-catalog/artifacts/bkaifu5k_logo.png"
                alt="AYMORENOV Logo"
                className="h-20 md:h-24 lg:h-28 w-auto object-contain mb-4"
              />
              <h1 className="text-4xl lg:text-6xl font-bold text-white leading-tight">
                {t('home.heroTitle')}<br />
                <span 
                  className="bg-gradient-to-r bg-clip-text text-transparent"
                  style={{ backgroundImage: "linear-gradient(135deg, #D4A86A 0%, #FFFFFF 100%)" }}
                >
                  {t('home.heroSubtitle')}
                </span>
              </h1>
            </div>
            <div className="mb-6">
              <h2 className="text-2xl lg:text-3xl font-bold uppercase tracking-wide" style={{ color: "#D4A86A" }}>
                {t('home.heroTagline')}<br />
                <span>{t('home.heroTaglineSecond')}</span>
              </h2>
            </div>
            <p className="text-xl text-white mb-8 max-w-3xl mx-auto leading-relaxed opacity-90">
              {t('home.heroDescription')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://wa.me/5065884919?text=Bonjour Mohamed, je suis intéressé par vos services de rénovation AYMORENOV. Pouvez-vous me contacter?"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg" 
                  className="text-white font-semibold px-8 py-4 text-lg rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl"
                  style={{ backgroundColor: "#25D366" }}
                >
                  {t('home.whatsappButton')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <Link to="/about">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-2 text-white hover:text-slate-800 px-8 py-4 text-lg rounded-xl transition-all duration-300 border-white hover:bg-white"
                >
                  {t('home.learnMoreButton')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Real Work Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: "#283747" }}>
              {t('home.realizationsTitle')}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {t('home.realizationsDescription')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Installation de planchers */}
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-xl shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_aymorenov-catalog/artifacts/1cvn1k7v_IMG_20250916_181221.jpg"
                  alt="Installation de planchers AYMORENOV" 
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="font-bold text-lg">{t('home.flooringInstallation')}</h3>
                  <p className="text-sm opacity-90">{t('home.flooringDescription')}</p>
                </div>
              </div>
            </div>
            
            {/* Travaux d'isolation et structure */}
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-xl shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_aymorenov-catalog/artifacts/2zuk74lb_IMG_20250916_181221%20%289%29.jpg"
                  alt="Travaux d'isolation AYMORENOV" 
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="font-bold text-lg">{t('home.insulationWork')}</h3>
                  <p className="text-sm opacity-90">{t('home.insulationDescription')}</p>
                </div>
              </div>
            </div>
            
            {/* Asphalte chaud et froid */}
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-xl shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_aymorenov-catalog/artifacts/ht9dqx0w_IMG-20250911-WA0048.jpg"
                  alt="Asphalte chaud et froid AYMORENOV" 
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="font-bold text-lg">{t('home.asphaltWork')}</h3>
                  <p className="text-sm opacity-90">{t('home.asphaltDescription')}</p>
                </div>
              </div>
            </div>
            
            {/* Rénovation de cuisine */}
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-xl shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_aymorenov-catalog/artifacts/jj5nsiov_IMG_20250916_181221%20%284%29.jpg"
                  alt="Rénovation de cuisine AYMORENOV" 
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="font-bold text-lg">{t('home.kitchenRenovation')}</h3>
                  <p className="text-sm opacity-90">{t('home.kitchenDescription')}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link to="/contact">
              <Button 
                size="lg"
                className="text-white font-semibold px-8 py-4 text-lg rounded-xl transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: "#DC2626" }}
              >
                {t('home.realizationsCTA')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              {t('home.servicesTitle')}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {t('home.servicesDescription')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-center mb-4">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center text-white mr-4"
                        style={{ background: "linear-gradient(135deg, #E53E3E 0%, #ED8936 100%)" }}
                      >
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-800">{service.title}</h3>
                    </div>
                    <p className="text-slate-600 mb-4">{service.description}</p>
                    <ul className="space-y-2 mb-4 flex-grow">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto">
                      <div className="text-lg font-semibold text-center mb-4" style={{ color: "#DC2626" }}>
                        {t('home.contactForQuote')}
                      </div>
                      <a 
                        href={`https://wa.me/5065884919?text=Bonjour, je suis intéressé par le service: ${service.title}. Pouvez-vous me donner plus d'informations et un devis?`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button 
                          className="w-full text-white font-medium transition-all duration-300 hover:scale-105"
                          style={{ backgroundColor: "#DC2626" }}
                        >
                          {t('home.whatsappQuote')}
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              {t('home.whyChooseTitle')}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {t('home.whyChooseDescription')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyValues.map((value, index) => {
              const icons = [Users, Award, Clock, CheckCircle];
              const Icon = icons[index];
              return (
                <div key={index} className="text-center group hover:scale-105 transition-transform duration-300">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white mx-auto mb-4"
                    style={{ background: "linear-gradient(135deg, #E53E3E 0%, #ED8936 100%)" }}
                  >
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              {t('home.testimonialsTitle')}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {t('home.testimonialsDescription')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full mr-4 object-cover"
                    />
                    <div>
                      <h4 className="font-semibold text-slate-800">{testimonial.name}</h4>
                      <p className="text-sm text-slate-600">{testimonial.location}</p>
                    </div>
                  </div>
                  <div className="flex items-center mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-3 italic">"{testimonial.comment}"</p>
                  <div className="text-sm text-slate-600">
                    <div className="font-medium">{testimonial.project}</div>
                    <div>{testimonial.date}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Add Comment Section */}
          <div className="mt-16 max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-slate-800 mb-4">
                {t('home.shareExperienceTitle')}
              </h3>
              <p className="text-slate-600">
                {t('home.shareExperienceDescription')}
              </p>
            </div>
            
            <Card className="shadow-lg">
              <CardContent className="p-8">
                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('home.yourName')} *
                      </label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder={t('home.enterName')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('home.city')}
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder={t('home.yourCity')}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('home.projectType')}
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder={t('home.projectPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('home.yourRating')} *
                    </label>
                    <div className="flex items-center space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star}
                          className="w-8 h-8 text-gray-300 hover:text-yellow-400 cursor-pointer transition-colors"
                        />
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('home.yourComment')} *
                    </label>
                    <textarea
                      required
                      rows={4}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder={t('home.commentPlaceholder')}
                    ></textarea>
                  </div>
                  
                  <div className="text-center">
                    <Button 
                      type="submit"
                      size="lg"
                      className="text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 hover:scale-105"
                      style={{ backgroundColor: "#DC2626" }}
                    >
                      {t('home.publishReview')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: "#283747" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Contact Information */}
            <div className="text-white">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                {t('home.ctaTitle')}
              </h2>
              <p className="text-xl text-slate-300 mb-8">
                {t('home.ctaDescription')}
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: "#D4A86A" }}
                  >
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold">{t('contact.name')}</p>
                    <a href="tel:506-588-4919" className="text-slate-300 hover:text-white transition-colors">
                      {t('contact.phone')}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: "#D4A86A" }}
                  >
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold">Email Direct</p>
                    <a href="mailto:aymorenov25@gmail.com" className="text-slate-300 hover:text-white transition-colors">
                      {t('contact.email')}
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://wa.me/5065884919?text=Bonjour Mohamed, je suis intéressé par vos services de rénovation AYMORENOV. Pouvez-vous me contacter pour discuter de mon projet?"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    size="lg" 
                    className="font-semibold px-8 py-4 text-lg rounded-xl transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: "#25D366", color: "white" }}
                  >
                    {t('home.whatsappDirect')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <Link to="/contact">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="border-2 border-white text-white hover:bg-white px-8 py-4 text-lg rounded-xl transition-all duration-300"
                    style={{ "--tw-hover-text": "#283747" }}
                    onMouseEnter={(e) => e.target.style.color = "#283747"}
                    onMouseLeave={(e) => e.target.style.color = "white"}
                  >
                    {t('home.contactForm')}
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Visual Element */}
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_aymorenov-catalog/artifacts/r3jp1bso_pics.png"
                  alt="Équipe AYMORENOV au travail" 
                  className="rounded-2xl shadow-2xl w-full"
                />
              </div>
              <div className="absolute -top-4 -left-4 w-full h-full rounded-2xl opacity-20"
                   style={{ backgroundColor: "#D4A86A" }}></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;